/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-07T08:44:05.541Z
 * 소스 파일: src/data/dto/patient-treatement-detail-list/patient-treatment-detail-list-reponse-dto copy
 */

import type { PatientTreatmentDetailListResponseDto } from "@/data/dto/patient-treatement-detail-list/patient-treatment-detail-list-reponse-dto copy";

/**
 * PatientTreatmentDetailListResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isPatientTreatmentDetailListResponseDto(input: unknown): input is PatientTreatmentDetailListResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.treatmentInfoUuid === "string" &&
    typeof obj.step === "number" && !isNaN(obj.step) &&
    typeof obj.state === "string" &&
    typeof obj.startDate === "string" &&
    typeof obj.endDate === "string" &&
    typeof obj.actuallyExecutedDay === "number" && !isNaN(obj.actuallyExecutedDay) &&
    typeof obj.recommendedExecutedDay === "number" && !isNaN(obj.recommendedExecutedDay) &&
    typeof obj.actuallyPlayTime === "number" && !isNaN(obj.actuallyPlayTime) &&
    typeof obj.recommendedTotalPlayTime === "number" && !isNaN(obj.recommendedTotalPlayTime) &&
    typeof obj.doctorName === "string" && obj.doctorName.trim() !== ""
  );
}

/**
 * PatientTreatmentDetailListResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validatePatientTreatmentDetailListResponseDto(input: unknown): IValidation<PatientTreatmentDetailListResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // treatmentInfoUuid (required)
      if (objObj.treatmentInfoUuid === undefined) {
        errors.push({ path: "treatmentInfoUuid", expected: "string", value: undefined });
      } else {
        if (typeof objObj.treatmentInfoUuid !== "string") {
          errors.push({ path: "treatmentInfoUuid", expected: "string", value: objObj.treatmentInfoUuid });
        }
      }
      // step (required)
      if (objObj.step === undefined) {
        errors.push({ path: "step", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.step === "number" && !isNaN(objObj.step))) {
          errors.push({ path: "step", expected: "number", value: objObj.step });
        }
      }
      // state (required)
      if (objObj.state === undefined) {
        errors.push({ path: "state", expected: "string", value: undefined });
      } else {
        if (typeof objObj.state !== "string") {
          errors.push({ path: "state", expected: "string", value: objObj.state });
        }
      }
      // startDate (required)
      if (objObj.startDate === undefined) {
        errors.push({ path: "startDate", expected: "string", value: undefined });
      } else {
        if (typeof objObj.startDate !== "string") {
          errors.push({ path: "startDate", expected: "string", value: objObj.startDate });
        }
      }
      // endDate (required)
      if (objObj.endDate === undefined) {
        errors.push({ path: "endDate", expected: "string", value: undefined });
      } else {
        if (typeof objObj.endDate !== "string") {
          errors.push({ path: "endDate", expected: "string", value: objObj.endDate });
        }
      }
      // actuallyExecutedDay (required)
      if (objObj.actuallyExecutedDay === undefined) {
        errors.push({ path: "actuallyExecutedDay", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.actuallyExecutedDay === "number" && !isNaN(objObj.actuallyExecutedDay))) {
          errors.push({ path: "actuallyExecutedDay", expected: "number", value: objObj.actuallyExecutedDay });
        }
      }
      // recommendedExecutedDay (required)
      if (objObj.recommendedExecutedDay === undefined) {
        errors.push({ path: "recommendedExecutedDay", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.recommendedExecutedDay === "number" && !isNaN(objObj.recommendedExecutedDay))) {
          errors.push({ path: "recommendedExecutedDay", expected: "number", value: objObj.recommendedExecutedDay });
        }
      }
      // actuallyPlayTime (required)
      if (objObj.actuallyPlayTime === undefined) {
        errors.push({ path: "actuallyPlayTime", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.actuallyPlayTime === "number" && !isNaN(objObj.actuallyPlayTime))) {
          errors.push({ path: "actuallyPlayTime", expected: "number", value: objObj.actuallyPlayTime });
        }
      }
      // recommendedTotalPlayTime (required)
      if (objObj.recommendedTotalPlayTime === undefined) {
        errors.push({ path: "recommendedTotalPlayTime", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.recommendedTotalPlayTime === "number" && !isNaN(objObj.recommendedTotalPlayTime))) {
          errors.push({ path: "recommendedTotalPlayTime", expected: "number", value: objObj.recommendedTotalPlayTime });
        }
      }
      // doctorName (required)
      if (objObj.doctorName === undefined) {
        errors.push({ path: "doctorName", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.doctorName === "string" && objObj.doctorName.trim() !== "")) {
          errors.push({ path: "doctorName", expected: "string (non-empty)", value: objObj.doctorName });
        }
      }
    }
  
  if (errors.length === 0 && isPatientTreatmentDetailListResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "PatientTreatmentDetailListResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * PatientTreatmentDetailListResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertPatientTreatmentDetailListResponseDto(input: unknown): PatientTreatmentDetailListResponseDto {
  const validation = validatePatientTreatmentDetailListResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertPatientTreatmentDetailListResponseDto] :: 검증 실패: PatientTreatmentDetailListResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
